import React from "react";
import { Entity } from "@common/types/entity";
import type { InvoiceTemplateListItem } from "@common/schemas/domains/invoice/invoice-template.schema";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import styles from "./SidePanel.module.scss";

export interface TemplateSelectProps {
    entities: Entity[];
    templates: InvoiceTemplateListItem[];
    isLoading?: boolean;
    isRequired?: boolean;
    selectedTemplateId?: string | null;
    isApplying?: boolean;
    onSelectTemplate: (templateId: string, templateEntityId: number) => void;
    // Opens the selected template in the template editor, so its branding and
    // wording can be fixed without hunting it down on the templates page.
    onEditTemplate?: () => void;
    // Closes the invoice drawer before navigating to the templates page.
    onClose?: () => void;
}

// Template picker at the top of the details panel: a card with the "Template"
// title and a searchable dropdown holding the selected template (entity icon +
// name). The list spans the active templates across every workspace entity
// (not just the invoice's From entity); the entity icon on each option tells
// them apart. The picker stays openable even with no templates so
// "Manage templates" is reachable from invoice creation.
export const TemplateSelectField: React.FC<TemplateSelectProps> = ({
    entities,
    templates,
    isLoading,
    isRequired,
    selectedTemplateId,
    isApplying,
    onSelectTemplate,
    onClose,
}) => {
    const navigate = useStateAwareNavigate();
    const entitiesById = new Map(entities.map((entity) => [entity.id, entity]));
    const selectedTemplate =
        templates.find((template) => template.id === selectedTemplateId) ??
        null;
    const selectedTemplateEntity = selectedTemplate
        ? entitiesById.get(selectedTemplate.entityId)
        : undefined;

    const templateItems: Array<PickerItemData<string>> = templates.map(
        (template) => {
            const templateEntity = entitiesById.get(template.entityId);
            return {
                value: template.id,
                label: template.name,
                labelHint: template.customerName ?? undefined,
                leading: templateEntity ? (
                    <EntityAvatar entity={templateEntity} size="xs" />
                ) : undefined,
            };
        },
    );

    let placeholder = "Select";
    if (isLoading) {
        placeholder = "Loading templates...";
    } else if (templates.length === 0) {
        placeholder = "No templates available";
    } else if (isRequired) {
        placeholder = "Selecting template...";
    }

    return (
        <div className={styles.unframedSection}>
            <div className={styles.cardTitle}>Template</div>
            <Picker.Select
                items={templateItems}
                searchable
                searchPlaceholder="Search templates..."
                emptyLabel="No matching templates"
                value={selectedTemplate?.id ?? null}
                disabled={isApplying || isLoading}
                onValueChange={(templateId) => {
                    const template = templates.find(
                        (item) => item.id === templateId,
                    );
                    if (template) {
                        onSelectTemplate(template.id, template.entityId);
                    }
                }}
                placeholder={placeholder}
                renderValue={() =>
                    selectedTemplate ? (
                        <span className={styles.templateCardValue}>
                            {selectedTemplateEntity && (
                                <EntityAvatar
                                    entity={selectedTemplateEntity}
                                    size="xs"
                                />
                            )}
                            <span className={styles.templateCardName}>
                                {selectedTemplate.name}
                            </span>
                        </span>
                    ) : undefined
                }
                trigger={
                    <Picker.Button
                        variant="primary"
                        size="xl"
                        block
                        aria-label="Template"
                    />
                }
                renderFooter={() => (
                    <Picker.Item
                        onSelect={() => {
                            onClose?.();
                            navigate("/invoicing/templates");
                        }}
                    >
                        Manage templates
                    </Picker.Item>
                )}
            />
        </div>
    );
};
